/**
 * One button component for the whole site.
 *
 * Renders a <button> by default and an <a> when given `href`, because a thing
 * that navigates must be a link — for middle-click, for "open in new tab", and
 * for screen readers, which announce the two differently.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.css';

type Variant = 'primary' | 'outline' | 'onNavy';
type Size = 'md' | 'lg';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> & {
    href?: undefined;
  };

type AnchorProps = CommonProps & {
  href: string;
  /** Set for links leaving the site; adds the security rel automatically. */
  external?: boolean;
  to?: undefined;
};

/** An in-app route. Renders a react-router <Link>, so no full page reload. */
type RouteProps = CommonProps & {
  to: string;
  href?: undefined;
};

function classesFor({ variant = 'primary', size = 'lg', block, className }: CommonProps): string {
  return [styles.base, styles[variant], styles[size], block ? styles.block : '', className ?? '']
    .filter(Boolean)
    .join(' ');
}

export function Button(props: ButtonProps | AnchorProps | RouteProps) {
  // An in-app route: a real <a> under the hood, but handled by the router.
  if ('to' in props && props.to !== undefined) {
    const { to, children, variant, size, block, className } = props;
    return (
      <Link to={to} className={classesFor({ variant, size, block, className, children })}>
        {children}
      </Link>
    );
  }

  if ('href' in props && props.href !== undefined) {
    const { href, external, children, variant, size, block, className, ...rest } = props;
    return (
      <a
        href={href}
        className={classesFor({ variant, size, block, className, children })}
        // noopener stops the new page reaching back through window.opener;
        // noreferrer stops it learning where the visitor came from.
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...rest}
      >
        {children}
      </a>
    );
  }

  const { children, variant, size, block, className, type = 'button', ...rest } = props;
  return (
    <button
      type={type}
      className={classesFor({ variant, size, block, className, children })}
      {...rest}
    >
      {children}
    </button>
  );
}
